import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CanPipe } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { cloneDeep } from 'lodash';
import {
  ButtonComponent,
  InputComponent,
  ModalService,
  SmartSearchComponent,
  TableCellDirective,
  TableGridComponent,
  TableHeader
} from 'mbs-ui-kit';
import { CompaniesQueryParams, Company, tableSelectAllEntitiesText, tableSelectedEntitiesText } from 'mbs-ui-shared';
import { AddCompanyModalComponent } from 'mbs-ui-shared@components/add-company/add-company-modal.component';
import { CompaniesFacade } from 'mbs-ui-shared@facades/companies.facade';
import { PermissionsEnum } from 'mbs-ui-shared@models/PermissionsEnum';
import { splitArrayIntoChunks } from 'mbs-ui-shared@modules/utility-selection-modal/const';
import { BehaviorSubject, EMPTY, Observable, debounceTime, filter, forkJoin, from, map, of, switchMap, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'mbs-select-company-utility',
  templateUrl: './select-company-utility.component.html',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    I18NextPipe,
    TableGridComponent,
    TableCellDirective,
    InputComponent,
    ButtonComponent,
    CanPipe
  ]
})
export class SelectCompanyUtilityComponent implements OnInit, OnChanges {
  private companiesFacade = inject(CompaniesFacade);
  private i18next = inject(I18NextPipe);
  private modalService = inject(ModalService);

  @ViewChild(SmartSearchComponent, { static: false }) smartSearch: SmartSearchComponent;
  @Input() selectedIds: string[] = [];
  @Input() multipleSelect = false;
  @Input() noDataText: string = null;
  // Companies that must stay selected (e.g. they already hold destinations of the account): checked, disabled, tooltip
  @Input() disabledIds: string[] = [];
  @Input() disabledHint: string = null;
  @Input() showAddNewCompany = false;
  @Output() emitChanges = new EventEmitter<Company[]>();

  public readonly permissionsEnum = PermissionsEnum;
  // Rows are locked via the grid's own bindDisabledValues so the shared companies store never sees a disabled flag
  public readonly disabledRowCondition = { condition: (row: Company) => this.isLocked(row) };
  public tooltipForDisabledRow: { text: string; placement: string; container: string } = null;

  public loading = false;

  public headers: TableHeader[] = [
    {
      name: 'Company name',
      overflow: true,
      class: 'pl-0',
      gridColSize: '10fr'
    }
  ];

  public companySearchForm = new FormGroup<{ name: FormControl<string> }>({
    name: new FormControl(null)
  });

  public infiniteScrollDistance = 0.5;
  public infiniteScrollThrottle = 150;
  public infiniteScrollLoading = false;
  public orderBy = 'name:asc';
  public total = 0;

  public data: Company[] = [];
  public selected: Company[] = [];

  public onScrolled$ = new BehaviorSubject<boolean>(false);

  private currentParams: CompaniesQueryParams = null;
  private limit = 100;
  private offset = 0;

  constructor() {
    this.getLoadStreamWithParams().subscribe({
      next: () => (this.loading = false),
      error: () => {
        this.loading = false;
        this.data = [];
      }
    });
  }

  updateStore(companies: Company[]) {
    this.companiesFacade.setEntities(companies);
  }

  loadSelected() {
    if (!this.selectedIds.length) return;

    this.loading = true;

    const getLimitedListStream$ = splitArrayIntoChunks(this.selectedIds).map((idList) =>
      this.companiesFacade.getCompanies({ ids: idList }).pipe(map((response) => response.data))
    );

    forkJoin(getLimitedListStream$)
      .pipe(
        tap(() => (this.loading = true)),
        map((response) => response.reduce((ac, next) => (ac ?? []).concat(next), null)),
        untilDestroyed(this)
      )
      .subscribe({
        next: (data: Company[]) => {
          this.selected = data;
          this.data = this.getNewDataAndKeepSelected(this.data);
          this.selectedIds = [];
          this.updateStore(this.selected);
          this.emitChanges.emit(this.selected);
          this.loading = false;
        },
        error: () => {
          this.selected = [];
          this.loading = false;
        }
      });
  }

  getNewDataAndKeepSelected(data: Company[]) {
    const selectedToBeAdded = this.selected?.filter((item) => data.every((dataItem) => dataItem.id !== item.id));
    return selectedToBeAdded.concat(data);
  }

  getLoadStreamWithParams(
    params: CompaniesQueryParams = { offset: this.offset, limit: this.limit, orderBy: this.orderBy },
    isInfiniteScroll = false
  ) {
    return this.companiesFacade.getCompanies(params).pipe(
      tap((response) => {
        this.currentParams = { ...params };
        this.total = response.total;
        const previousLength = this.data.length;
        if (params.offset > 0) {
          this.data = this.data.concat(response.data.filter((item) => this.data.every((dataItem) => dataItem.id !== item.id)));
        } else {
          this.data = this.getNewDataAndKeepSelected(response.data);
        }

        if (isInfiniteScroll && previousLength === this.data.length) this.onScrolled$.next(true);

        this.updateStore(this.data);
      })
    );
  }

  ngOnInit(): void {
    this.onScrolled$
      .pipe(
        filter((value) => value && this.data.length < this.total),
        tap(() => (this.infiniteScrollLoading = true)),
        switchMap(() =>
          this.getLoadStreamWithParams(
            { ...this.currentParams, orderBy: this.orderBy, offset: this.currentParams.offset + this.limit },
            true
          )
        ),
        untilDestroyed(this)
      )
      .subscribe({
        next: () => (this.infiniteScrollLoading = false),
        error: () => {
          this.infiniteScrollLoading = false;
          this.data = [];
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedIds) {
      this.loadSelected();
    }

    if (changes.disabledHint) {
      this.tooltipForDisabledRow = this.disabledHint ? { text: this.disabledHint, placement: 'top-left', container: 'body' } : null;
    }
  }

  addNewCompany(): void {
    from(this.modalService.openCustom<Company>(AddCompanyModalComponent))
      .pipe(
        catchError(() => EMPTY),
        filter(Boolean),
        untilDestroyed(this)
      )
      .subscribe((company) => {
        this.data = [company, ...this.data];
        this.total += 1;

        if (this.multipleSelect) {
          this.selected = [...this.selected, company];
          this.emitChanges.emit(this.selected);
        }

        this.updateStore(this.data);
      });
  }

  public isLocked = (row: Company): boolean => !!this.disabledIds?.length && this.disabledIds.includes(row.id);

  clear() {
    this.search();
  }

  search() {
    of(this.companySearchForm.get('name').value)
      .pipe(
        switchMap((name) => {
          if (name) {
            this.currentParams.search = name;
          } else {
            delete this.currentParams.search;
          }

          this.loading = true;

          return this.getLoadStreamWithParams({ ...this.currentParams, offset: 0, limit: this.limit, orderBy: this.orderBy });
        }),
        untilDestroyed(this)
      )
      .subscribe({
        next: () => (this.loading = false),
        error: () => {
          this.loading = false;
          this.data = [];
        }
      });
  }

  onSelectedRowsChanged(items: Company[]) {
    this.selected = items;
    this.emitChanges.emit(this.selected);
  }

  public trackBy(item) {
    return item.id;
  }

  public selectedText(): string {
    return tableSelectedEntitiesText({
      i18nPipe: this.i18next,
      count: this.selected.length,
      isSelectAllOnAllPages: this.selected.length === this.total,
      entityPluralKey: 'shared:common.company'
    });
  }

  public selectAllText(): string {
    return tableSelectAllEntitiesText({
      i18nPipe: this.i18next,
      total: this.total,
      entityPluralKey: 'shared:common.company'
    });
  }

  public selectAllOnAllPages(should: boolean) {
    if (should && this.total !== this.data.length) {
      const streams$: Observable<any>[] = [];
      const amountOfLoads = Math.round((this.total - this.data.length) / this.limit);

      for (let i = 1; i <= amountOfLoads; i++)
        streams$.push(
          this.getLoadStreamWithParams({
            ...this.currentParams,
            orderBy: this.orderBy,
            offset: this.currentParams.offset + i * this.limit
          })
        );

      this.loading = true;

      forkJoin(streams$)
        .pipe(debounceTime(300), untilDestroyed(this))
        .subscribe({
          next: () => {
            this.total = this.data.length;
            this.selected = cloneDeep(this.data);
            this.loading = false;
            this.emitChanges.emit(this.selected);
          },
          error: () => {
            this.loading = false;
          }
        });
    }
  }
}
